import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import CustomerModal from "../components/CustomerModal";

import { toast } from "react-toastify";

import {
  Search,
  Plus,
  Users,
  Copy,
  Pencil,
  Trash2,
  Wifi,
  Zap,
  Phone,
  MapPin,
  CreditCard,
} from "lucide-react";

import "../styles/CustomersPage.css";
import logo from "../assets/logo.png";

const CustomerPage = () => {
  const [view, setView] =
    useState("enetworks");

  const [customers, setCustomers] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [showModal, setShowModal] =
    useState(false);

  const [editCustomer, setEditCustomer] =
    useState(null);

  const BASE_URL =
    "https://client-management-backend-8x91.onrender.com";


const [savingCustomer, setSavingCustomer] =
  useState(false);

const [customerSaved, setCustomerSaved] =
  useState(false);


  const fetchCustomers = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/service-customers?type=${view}`
      );

      const data = await res.json();

      setCustomers(data);
    } catch (err) {
      console.error(err);

      toast.error(
        "Failed to load customers"
      );
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [view]);

const filteredData = useMemo(() => {
  return customers
    .filter(
      (item) =>
        item.customer_name
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          ) ||
        item.account_number
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          )
    )

    .sort((a, b) =>
      a.customer_name.localeCompare(
        b.customer_name
      )
    );
}, [customers, searchTerm]);

  const handleAddCustomer =
    async (type, formData) => {
      const payload = {
        customer_name:
          formData.customer,

        account_number:
          formData.account,

        address:
          formData.address,

        phone: formData.phone,

        service_type: type,
      };

      try {
        if (editCustomer) {
          await fetch(
            `${BASE_URL}/service-customers/${editCustomer.id}`,
            {
              method: "PUT",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body: JSON.stringify(
                payload
              ),
            }
          );

          toast.success(
            "Customer updated"
          );
        } else {
          const res = await fetch(
            `${BASE_URL}/service-customers`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body: JSON.stringify(
                payload
              ),
            }
          );

          if (res.status === 409) {
            toast.warning(
              "Account already exists"
            );

            return;
          }

          toast.success(
            "Customer added"
          );
        }

        fetchCustomers();
      } catch (err) {
        console.error(err);

        toast.error(
          "Failed to save customer"
        );
      }

    };

  const handleDelete = async (id) => {
    const confirmDelete =
      window.confirm(
        "Delete this customer?"
      );

    if (!confirmDelete) return;

    try {
      await fetch(
        `${BASE_URL}/service-customers/${id}`,
        {
          method: "DELETE",
        }
      );

      toast.success(
        "Customer deleted"
      );

      fetchCustomers();
    } catch (err) {
      console.error(err);

      toast.error(
        "Delete failed"
      );
    }
  };

  const handleEdit = (customer) => {
    setEditCustomer(customer);

    setShowModal(true);
  };

  const handleCopyAccount =
    async (account) => {
      try {
        await navigator.clipboard.writeText(
          account
        );

        toast.success(
          "Account copied"
        );
      } catch {
        toast.error(
          "Copy failed"
        );
      }
    };

  const providerLabel =
    view === "enetworks"
      ? "Enetworks"
      : "GPL Prepaid";

  const providerIcon =
    view === "enetworks" ? (
      <Wifi size={18} />
    ) : (
      <Zap size={18} />
    );


const displayedCustomers =
  searchTerm.trim()
    ? filteredData
    : filteredData.slice(0, 10);






  return (
    <div className="modern-customer-page">
      {/* HERO */}

      <div className="modern-customer-hero">
        <div className="modern-customer-hero-left">
          <div className="modern-customer-pill">
            {providerIcon}

            <span>
              Customer Registry
            </span>
          </div>

          <h1>
            Service Customer
            Console.
          </h1>

          <p>
            Manage utility
            customers, account
            records, service
            information and
            customer lookup from
            one modern dashboard.
          </p>

          <div className="modern-customer-provider-toggle">
            <button
              className={
                view ===
                "enetworks"
                  ? "active"
                  : ""
              }
              onClick={() => {
                setView(
                  "enetworks"
                );

                setSearchTerm("");
              }}
            >
              <Wifi size={16} />
              Enetworks
            </button>

            <button
              className={
                view === "gpl"
                  ? "active"
                  : ""
              }
              onClick={() => {
                setView("gpl");

                setSearchTerm("");
              }}
            >
              <Zap size={16} />
              GPL Prepaid
            </button>
          </div>
        </div>

        <div className="modern-customer-hero-right">
          <div className="modern-customer-summary-card">
            <div className="summary-icon teal">
              <Users size={22} />
            </div>

            <div>
              <span>
                Total Customers
              </span>

              <h2>
                {
                  customers.length
                }
              </h2>
            </div>
          </div>

          <div className="modern-customer-summary-card">
            <div className="summary-icon blue">
              {providerIcon}
            </div>

            <div>
              <span>
                Current Provider
              </span>

              <h2>
                {providerLabel}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}

      <div className="modern-customer-toolbar">
        <div className="modern-customer-search">
          <Search size={18} />

          <input
            type="text"
            placeholder="Search customer or account number..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />
        </div>

        <button
          className="modern-add-customer-btn"
          onClick={() => {
            setShowModal(true);

            setEditCustomer(null);
          }}
        >
          <Plus size={18} />
          Add Customer
        </button>
      </div>

      {/* TABLE */}

      <div className="modern-customer-table-wrapper">
        <table className="modern-customer-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>
                Account Number
              </th>
              <th>Address</th>
              <th>Phone</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.length >
            0 ? (
              filteredData.map(
                (row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="modern-customer-name-cell">
                        <div className="customer-avatar">
                          {row.customer_name
                            ?.charAt(0)
                            ?.toUpperCase()}
                        </div>

                        <div>
                          <h4>
                            {
                              row.customer_name
                            }
                          </h4>

                          <span>
                            {
                              providerLabel
                            }
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="modern-account-pill">
                        <span>
                          {
                            row.account_number
                          }
                        </span>

                        <button
                          onClick={() =>
                            handleCopyAccount(
                              row.account_number
                            )
                          }
                        >
                          <Copy
                            size={
                              15
                            }
                          />
                        </button>
                      </div>
                    </td>

                    <td>
                      <div className="modern-info-cell">
                        <MapPin
                          size={16}
                        />

                        <span>
                          {
                            row.address
                          }
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="modern-info-cell">
                        <Phone
                          size={16}
                        />

                        <span>
                          {
                            row.phone
                          }
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="modern-customer-actions">
                        <button
                          className="action-btn edit"
                          onClick={() =>
                            handleEdit(
                              row
                            )
                          }
                        >
                          <Pencil
                            size={
                              16
                            }
                          />
                        </button>

                        <button
                          className="action-btn delete"
                          onClick={() =>
                            handleDelete(
                              row.id
                            )
                          }
                        >
                          <Trash2
                            size={
                              16
                            }
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="modern-no-data"
                >
                  <CreditCard
                    size={42}
                  />

                  <h3>
                    No customers
                    found
                  </h3>

                  <p>
                    No matching
                    customer
                    records were
                    located.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}

{/* MODERN CUSTOMER MODAL */}

{showModal && (
  <div className="customer-inline-modal-overlay">
    <div className="customer-inline-modal-container">

      {/* HEADER */}

      <div className="customer-inline-modal-top">
        <div className="customer-inline-modal-pill">
          {view === "enetworks"
            ? "ENETWORKS"
            : "GPL PREPAID"}
        </div>

        <button
          className="customer-inline-close-btn"
          onClick={() => {
            setShowModal(false);
            setEditCustomer(null);
          }}
        >
          ✕
        </button>
      </div>

      {/* HERO */}

      <div className="customer-inline-modal-hero">
        <h2>
          {editCustomer
            ? "Edit Customer"
            : "Add New Customer"}
        </h2>

        <p>
          Create and manage utility
          customer records securely
          within.
        </p>
      </div>


{savingCustomer && (
  <div className="customer-inline-loader-screen">

    <div className="customer-inline-modern-loader">

      <div className="customer-inline-loader-ring" />

      <div className="customer-inline-loader-ring-two" />

      <div className="customer-inline-loader-center">
        <img
          src={logo}
          alt="RCS Logo"
        />
      </div>

    </div>

    <h2>
      Creating Customer
    </h2>

    <p>
      Processing account details,
      validating customer records
      and preparing service profile...
    </p>

  </div>
)}

{customerSaved && (
  <div className="customer-inline-success-screen">

    <div className="customer-inline-success-tick">
      ✓
    </div>

    <h2>
      Customer Saved
    </h2>

    <p>
      Customer profile was
      successfully created.
    </p>

    <button
      className="customer-inline-success-btn"
      onClick={() => {
        setCustomerSaved(false);

        setShowModal(false);

        setEditCustomer(null);
      }}
    >
      Close
    </button>
  </div>
)}



{/* FORM */}
{!savingCustomer &&
 !customerSaved && (


<form
  className="customer-inline-form"
onSubmit={async (e) => {
  e.preventDefault();

  setSavingCustomer(true);

  const formData = {
    customer:
      e.target.customer.value,

    account:
      e.target.account.value,

    address:
      e.target.address.value,

    phone:
      e.target.phone.value,
  };

  await new Promise((resolve) =>
    setTimeout(resolve, 5000)
  );

  await handleAddCustomer(
    view,
    formData
  );

  setSavingCustomer(false);

  setCustomerSaved(true);
}}
>

  <div className="customer-inline-group">
    <label>
      Customer Name
    </label>

    <input
      type="text"
      name="customer"
      placeholder="Enter customer name"
      defaultValue={
        editCustomer?.customer_name ||
        ""
      }
      required
    />
  </div>

  <div className="customer-inline-group">
    <label>
      Account Number
    </label>

    <input
      type="text"
      name="account"
      placeholder="Enter account number"
      defaultValue={
        editCustomer?.account_number ||
        ""
      }
      required
    />
  </div>

  <div className="customer-inline-group">
    <label>
      Address
    </label>

    <input
      type="text"
      name="address"
      placeholder="Enter address"
      defaultValue={
        editCustomer?.address ||
        ""
      }
    />
  </div>

  <div className="customer-inline-group">
    <label>
      Phone Number
    </label>

    <input
      type="text"
      name="phone"
      placeholder="Enter phone number"
      defaultValue={
        editCustomer?.phone ||
        ""
      }
    />
  </div>

  <div className="customer-inline-actions">

    <button
      type="button"
      className="customer-inline-cancel"
      onClick={() => {
        setShowModal(false);
        setEditCustomer(null);
      }}
    >
      Cancel
    </button>

    <button
      type="submit"
      className="customer-inline-save"
    >
      {editCustomer
        ? "Update Customer"
        : "Save Customer"}
    </button>
  </div>
</form>
)}
    </div>
  </div>
)}


    </div>
  );
};

export default CustomerPage;